import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { hitGetChatHistoryClientApi } from "../../../Redux/Client/ChatListClientSlice";
import { hitGetChatDetailsClientApi } from "../../../Redux/Client/ChatDetailsClientSlice";
import { timeAgo } from "../../../Helper/CommonFunctions";
import io from "socket.io-client";
import Loader from "../../Loader/Loader"; // adjust the path
import "./Messages.scss";
import { hitUploadMedia } from "../../../Redux/Common/UploadMediaSlice";
import { showToast } from "../../../Helper/Toast";
import profileImagePlaceholder from "../../../assets/img/profileIcon.png";
import searchIcon from "../../../assets/img/searchIcon.svg";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";
import { ApiBaseUrl } from "../../../Utils/Constants";

const Messages = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const chatIdFromRequest = location?.state?.chatId; // safely access chatId

  const {
    setUnreadMessagesCount,
    setUnreadNotificationsCount,
    unreadMessagesCount,
    unreadNotificationsCount,
  } = useGlobalState();
  const { setGlobalLoading } = useGlobalState();

  const [selectedChat, setSelectedChat] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [file, setFile] = useState(null);
  const [chatListData, setChatListData] = useState([]);
  const [currentOpenChat, setCurrentOpenChat] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar toggle
  const [isUploading, setIsUploading] = useState(false);

  const [clientId, setClientId] = useState(localStorage.getItem("clientId"));
  const [chatId, setChatId] = useState("");
  const [jobId, setJobId] = useState("");
  const [bidId, setBidId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [messages, setMessages] = useState([]);

  const [localChatSideList, setLocalChatSideList] = useState([]);
  const chatHistoryData = useSelector(
    (state) => state.getChatHistoryClientReducer.data
  );

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (localStorage.getItem("clientToken") === null) {
      navigate("/client/login");
    } else {
      dispatch(hitGetChatHistoryClientApi());
    }
  }, []);

  useEffect(() => {
    if (chatHistoryData?.status === true && chatHistoryData.data.length > 0) {
      setLoading(false);
      setChatListData(chatHistoryData.data);
      setLocalChatSideList(chatHistoryData.data);
      setProfessionalId(chatHistoryData.data[0]?.professionalId?._id);
      setChatId(chatHistoryData.data[0]?._id);
      setJobId(chatHistoryData.data[0]?.jobId?._id);
      setBidId(chatHistoryData.data[0]?.bidId?._id);
      setClientId(chatHistoryData.data[0]?.userId?._id);
      setMessages(chatHistoryData.data[0]?.messages || []);

      if (chatHistoryData.data.length > 0) {
        setSelectedChat(chatHistoryData.data[0]._id);
        // set isReadByUser to 1 for the selected chat
        // find index based on _id
        const index = localChatSideList.findIndex(
          (chat) => chat._id === chatHistoryData.data[0]._id
        );

        if (index !== -1) {
          const updatedList = localChatSideList.map((chat, i) => {
            if (i === index) {
              return {
                ...chat,
                messages: [
                  {
                    ...chat.messages[0],
                    isReadByUser: 1,
                  },
                  ...chat.messages.slice(1),
                ],
              };
            }
            return chat;
          });

          setLocalChatSideList(updatedList);
        }

        dispatch(
          hitGetChatDetailsClientApi({
            chatId:
              chatIdFromRequest !== undefined
                ? chatIdFromRequest
                : chatHistoryData.data[0]?._id,
          })
        ).then((response) => {
          if (response.payload.status === true) {
            let unreadCount = 0;
            response.payload.data.messages.forEach((msg) => {
              if (msg.isReadByUser === 0) {
                unreadCount++;
              }
            });
            setUnreadMessagesCount((prevCount) => prevCount - unreadCount);
            setCurrentOpenChat(chatHistoryData.data[0]);
            setMessages(response.payload.data.messages || []);
          }
        });
      }
    }
  }, [chatHistoryData]);

  useEffect(() => {
    const newSocket = io(ApiBaseUrl.replace(/\/$/, ""));
    setSocket(newSocket);

    newSocket.on("connect", () => {
      newSocket.emit("user_connected", {
        type: 2,
        userId: clientId,
        socketId: newSocket.id,
      });
    });

    newSocket.on("message", (data) => {
      setMessages((prevMessages) => [...prevMessages, data]);
    });

    return () => {
      newSocket.close();
    };
  }, [professionalId]);

  useEffect(() => {
    if (file) {
      uploadFile();
    }
  }, [file]);

  useEffect(() => {
    if (searchQuery === "") {
      dispatch(hitGetChatDetailsClientApi());
    }
  }, [searchQuery]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        showToast("File size should be less than 5MB", "error");
        return;
      }
      setFile(selectedFile);
    }
  };

  const uploadFile = async () => {
    if (file && !isUploading) {
      setIsUploading(true);
      setLoading(true);
      setGlobalLoading(true);
      const formData = new FormData();
      formData.append("myfile", file);
      try {
        const response = await dispatch(hitUploadMedia(formData));
        setLoading(false);
        setGlobalLoading(false);
        if (response.payload.status === true) {
          showToast("File uploaded successfully!", "success");
          let message = {
            type: 2,
            professionalId: professionalId,
            userId: clientId,
            chatId: chatId,
            jobId: jobId,
            messageFor: 1,
            bidId: bidId,
            url: response.payload.data[0].url,
            message: "",
            messageType: 2,
            sentAt: new Date(),
          };
          socket.emit("send_message", message);
          setMessages([...messages, message]);
          setMessageInput("");
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          showToast(response.payload.message, "error");
        }
      } catch (error) {
        setLoading(false);
        setGlobalLoading(false);
      }
    }
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !socket) return;

    let message = {
      type: 2,
      professionalId: professionalId,
      userId: clientId,
      chatId: chatId,
      messageFor: 1,
      jobId: jobId,
      bidId: bidId,
      message: messageInput,
    };

    socket.emit("send_message", message);

    const newMessage = {
      _id: messages.length + 1,
      message: messageInput,
      sentAt: new Date(),
      messageFor: 1,
      messageType: 1,
    };
    setMessages([...messages, newMessage]);
    setMessageInput("");

    dispatch(hitGetChatHistoryClientApi()).then(() => {
      const updatedChatHistory = chatListData.map((chat) => {
        if (chat._id === chatId) {
          return {
            ...chat,
            messages: [newMessage, ...chat.messages],
          };
        }
        return chat;
      });
      setLocalChatSideList(updatedChatHistory);
      // Optionally update localChatSideList here if needed
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const enterForSearchHandler = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      let newFilterArray = chatListData.filter(
        (chat) =>
          chat.userId.firstName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          chat.userId.lastName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setChatListData(newFilterArray);
    }
  };

  const handleChatDetailClick = (chatData) => {
    setSelectedChat(chatData._id);
    setProfessionalId(chatData.professionalId?._id);
    setChatId(chatData._id);
    setJobId(chatData.jobId?._id);
    setBidId(chatData.bidId?._id);
    setClientId(chatData.userId?._id);
    setIsSidebarOpen(false); // Close sidebar on mobile after selection

    // find index based on _id
    const index = localChatSideList.findIndex(
      (chat) => chat._id === chatData._id
    );

    //console.log("Clicked chat index:", index);
    if (index !== -1) {
      const updatedList = localChatSideList.map((chat, i) => {
        if (i === index) {
          return {
            ...chat,
            messages: [
              {
                ...chat.messages[0],
                isReadByUser: 1,
              },
              ...chat.messages.slice(1),
            ],
          };
        }
        return chat;
      });

      setLocalChatSideList(updatedList);
    }

    dispatch(hitGetChatDetailsClientApi({ chatId: chatData._id })).then(
      (response) => {
        if (response.payload.status === true) {
          let unreadCount = 0;
          response.payload.data.messages.forEach((msg) => {
            if (msg.isReadByUser === 0) {
              unreadCount++;
            }
          });
          setUnreadMessagesCount((prevCount) => prevCount - unreadCount);
          setCurrentOpenChat(chatData);
          setMessages(response.payload.data.messages || []);
        }
      }
    );
  };

  // ✅ Use your Loader here
  // if (loading) {
  //   return <Loader />;
  // }

  if (loading) {
    setGlobalLoading(true);
    return <div style={{ height: "100vh" }}><Loader /></div>;
  }

  return (
    <>
      <div
        style={{
          overflow: "hidden",
          position: "fixed",
          width: "-webkit-fill-available",
          height: "100%",
          top: 0,
          left: 0,
        }}
      >
        <div className="custom_container">
          <div className="chat-app">
            {/* Mobile Header */}
            <div className="mobile-header">
              <button
                className="mobile-menu-btn"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                aria-label="Toggle menu"
              >
                ☰
              </button>
            </div>

            {/* Sidebar */}
            <div className={`chat-sidebar ${isSidebarOpen ? "open" : ""}`}>
              <div className="sidebar-header">
                <div className="search-container">
                  <span className="search-icon">
                    {" "}
                    <img src={searchIcon} alt="Search" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search messages"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={enterForSearchHandler}
                    className="search-input"
                  />
                </div>
              </div>

              <div className="conversations-list conversations-list-scroll">
                {localChatSideList.length > 0 ? (
                  localChatSideList.map((conv) => (
                    <div
                      key={conv._id}
                      className={`conversation-item 
      ${selectedChat === conv._id ? "active" : ""} 
      ${conv.messages[0].isReadByUser === 0 ? "highlight-border" : ""}
    `}
                      onClick={() => handleChatDetailClick(conv)}
                    >
                      <div className="conversation-avatar">
                        <img
                          src={
                            conv.professionalId.profileImage !== ""
                              ? conv.professionalId.profileImage
                              : profileImagePlaceholder
                          }
                          alt={`${conv.professionalId.firstName} ${conv.professionalId.lastName}`}
                        />
                      </div>
                      <div className="conversation-info">
                        <div className="conversation-header">
                          <h4 className="conversation-name">
                            {conv.jobId.title}
                          </h4>
                          <span className="conversation-time">
                            {conv.messages && conv.messages.length > 0
                              ? timeAgo(conv.messages[0].sentAt)
                              : ""}
                          </span>
                        </div>
                        <p className="conversation-message">
                          {conv.messages && conv.messages.length > 0
                            ? conv.messages[0].messageType === 1 ||
                              !conv.messages[0].messageType
                              ? conv.messages[0].message
                              : "📎 Attachment"
                            : "No messages"}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-chats">
                    <p>No conversations yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Overlay for mobile */}
            {isSidebarOpen && (
              <div
                className="sidebar-overlay"
                onClick={() => setIsSidebarOpen(false)}
              />
            )}

            {/* Main Chat Area */}
            {/* {currentOpenChat ? ( */}
            <div className="chat-main">
              {/* Chat Header */}
              <div className="chat-header">
                <button
                  className="mobile-back-btn"
                  onClick={() => setIsSidebarOpen(true)}
                  aria-label="Back to conversations"
                >
                  ←
                </button>

                {chatListData.length > 0 && (
                  <div className="chat-header-info">
                    <div className="chat-avatar">
                      <img
                        src={currentOpenChat?.professionalId?.profileImage}
                        alt={`${currentOpenChat?.professionalId?.firstName} ${currentOpenChat?.professionalId?.lastName}`}
                      />
                    </div>
                    <div className="chat-user-info">
                      <h3 className="chat-user-name">
                        {currentOpenChat?.professionalId?.firstName}{" "}
                        {currentOpenChat?.professionalId?.lastName}
                      </h3>
                      <span className="chat-user-status">
                        {currentOpenChat?.professionalId.socketId !== ""
                          ? "Active now"
                          : "Offline"}
                      </span>
                    </div>
                  </div>
                )}
                {/* <button className="chat-menu-btn" aria-label="Menu">
              ⋮
            </button> */}
              </div>

              {/* Messages Area */}
              <div className="messages-area">
                {messages.length > 0 ? (
                  messages.map((message) => (
                    <div
                      key={message._id}
                      className={`message ${message.messageFor === 1 ? "sent" : "received"
                        }`}
                    >
                      <div className="message-bubble">
                        {message.messageType === 1 || !message.messageType ? (
                          <p className="message-text">{message.message}</p>
                        ) : (
                          <img
                            src={message.url}
                            alt="attachment"
                            className="message-image"
                          />
                        )}
                        <span className="message-time">
                          {timeAgo(message.sentAt)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-messages">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              {chatListData.length > 0 && (
                <div className="message-input-container">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="file-input-hidden"
                    id="file-input"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="file-input"
                    className={`file-input-btn ${isUploading ? "disabled" : ""
                      }`}
                    title="Attach file"
                  >
                    {isUploading ? "⏳" : "📎"}
                  </label>

                  <textarea
                    placeholder="Type your message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="message-input resize-none overflow-hidden"
                    rows={1}
                  />

                  <button
                    className="send-btn"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    aria-label="Send message"
                  >
                    <span className="send-icon">➤</span>
                  </button>
                </div>
              )}
            </div>
            {/* ) : (
          <div className="chat-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">💬</div>
              <h3>Select a conversation</h3>
              <p>Choose a chat from the sidebar to start messaging</p>
            </div>
          </div>
        )} */}
          </div>
        </div>
      </div>

      {/* chat card-1 */}
      {/* <div className="payment-card">
        <div className="payment-card__content">
          <div className="payment-card__header">
            <img
              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
              alt="Sarah"
              className="payment-card__avatar"
            />
            <div className="payment-card__title-section">
              <h3 className="payment-card__title">New Payment for Job #1001</h3>
              <p className="payment-card__description">
                Sarah has completed a payment for Job #1001 per your request.
              </p>
              <button className="payment-card__button">View</button>
            </div>
          </div>
        </div>
      </div> */}

      {/* chat card-2 */}
      {/* <div className="payment-card">
        <div className="payment-card__content">
          <div className="payment-card__header">
            <img
              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
              alt="Sarah"
              className="payment-card__avatar"
            />
            <div className="payment-card__title-section">
              <h3 className="payment-card__title">New Payment for Job #1001</h3>
              <p className="payment-card__description">
                Sarah has completed a payment for Job #1001 per your request.
              </p>
              <p className="payment-card__description_2">
                Sarah has completed a payment for Job #1001 per your request.
              </p>
            </div>
          </div>
        </div>
      </div> */}
    </>
  );
};

export default Messages;
